import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { isoDate } from '@/lib/format';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';

/**
 * 타유저 공개 매매일지 — 금융 정보(매수가/매도가/수량/실현손익/수익률)는 노출 금지.
 * 노출 가능: 종목명, 매수/매도 여부, 날짜, 메모.
 */
interface PublicDiaryRow {
  id: number;
  ticker: string;
  asset_type: 'stock' | 'coin';
  ticker_name: string | null;
  buy_date: string | null;
  sell_date: string | null;
  memo: string | null;
  is_certified: boolean;
  created_at: string;
  user_id: string;
  nickname: string;
}

export function UserPublicDiaryPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['diaries', 'public', 'user', userId],
    queryFn: () =>
      get<{ userId: string; nickname: string | null; list: PublicDiaryRow[] }>(
        `/diaries/public/user/${userId}`,
      ),
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-6">
        <Skeleton className="mb-4 h-10 w-1/2" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const list = data?.list ?? [];
  const nickname = data?.nickname ?? '';

  return (
    <div className="mx-auto max-w-4xl px-5 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-extrabold">
          {nickname ? `${nickname} 님의 매매일지` : '공개 매매일지'}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          공개로 등록된 매매 내역만 표시돼요. 금융 정보(매수/매도가, 수량, 손익)는 공개되지 않아요.
        </p>
      </header>

      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
          공개된 매매일지가 아직 없어요.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((d) => (
            <PublicDiaryCard
              key={d.id}
              row={d}
              onClick={() =>
                navigate(`/diary/public/${encodeURIComponent(d.ticker)}`)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PublicDiaryCard({
  row,
  onClick,
}: {
  row: PublicDiaryRow;
  onClick: () => void;
}) {
  const hasSell = !!row.sell_date;
  const hasBuy = !!row.buy_date;
  const tradeBadge = hasSell
    ? {
        label: '매도',
        cls: 'bg-[var(--color-rise)]/10 text-[var(--color-rise)] border-[var(--color-rise)]/20',
      }
    : {
        label: '매수',
        cls: 'bg-[var(--color-fall)]/10 text-[var(--color-fall)] border-[var(--color-fall)]/20',
      };
  const statusBadge = hasSell
    ? {
        label: '청산',
        cls: 'bg-[var(--color-rise)]/10 text-[var(--color-rise)] border-[var(--color-rise)]/20',
      }
    : {
        label: '보유 중',
        cls: 'bg-[var(--color-fall)]/10 text-[var(--color-fall)] border-[var(--color-fall)]/20',
      };

  /* 종목명: COIN_NAMES_KR → ticker_name → stripKrw(코인) → 코드 */
  const isCoin = row.asset_type === 'coin';
  const displayName =
    (isCoin ? COIN_NAMES_KR[row.ticker] : null) ??
    row.ticker_name ??
    (isCoin ? stripKrw(row.ticker) : row.ticker);
  const showCodeChip = displayName !== row.ticker;

  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-colors hover:border-[var(--color-primary)]/40"
    >
      <div className="flex items-start justify-between gap-3">
        {/* 왼쪽: 종목 정보 */}
        <div className="min-w-0 flex-1">
          {/* 상태 배지 + 매수/매도 태그 + 종목명 */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex min-w-[52px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusBadge.cls}`}
            >
              {statusBadge.label}
            </span>
            <span
              className={`inline-flex min-w-[44px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tradeBadge.cls}`}
            >
              {tradeBadge.label}
            </span>
            <span className="truncate font-bold">{displayName}</span>
            {showCodeChip && (
              <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-sub)]">
                {row.ticker}
              </span>
            )}
          </div>

          {/* 자산 종류 */}
          <div className="mt-2 text-xs text-[var(--color-text-sub)]">
            {row.asset_type === 'coin' ? '코인' : '주식'}
          </div>

          {/* 날짜: 매수일 / 매도일 */}
          <div className="mt-1 space-y-0.5 text-[11px] text-[var(--color-text-sub)]">
            {hasBuy && (
              <div>
                매수일 :{' '}
                <span className="text-[var(--color-text)]">
                  {isoDate(row.buy_date as string)}
                </span>
              </div>
            )}
            {hasSell && (
              <div>
                매도일 :{' '}
                <span className="text-[var(--color-text)]">
                  {isoDate(row.sell_date as string)}
                </span>
              </div>
            )}
            {!hasBuy && !hasSell && (
              <div>
                등록일 :{' '}
                <span className="text-[var(--color-text)]">{isoDate(row.created_at)}</span>
              </div>
            )}
          </div>

          {/* 메모 */}
          {row.memo && (
            <p className="mt-2 line-clamp-2 text-sm text-[var(--color-text-sub)]">
              {row.memo}
            </p>
          )}
        </div>

        {/* 오른쪽: 화살표 */}
        <ChevronRight className="h-5 w-5 flex-shrink-0 text-[var(--color-text-sub)]" />
      </div>
    </button>
  );
}
